import footprint, { FootprintComponentKind } from "@onefootprint/footprint-js";
import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import {
  IPaymentCard,
  PaymentCardType
} from "@wingspanhq/payments/dist/interfaces";
import {
  PayoutDestinationType,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { useWSMutation } from "@ws-react-query";
import { useCallback, useMemo } from "react";
import { payoutsApi } from "@api";
import { queriesPaymentCards, queriesPayouts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { useSession } from "@hooks/useSession";
import { selectorPaymentCardPayoutReady } from "../selectors/selectorPaymentCardPayoutReady";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { strongETag } from "@utils/strongETag";
import { useMutationCompleteOneTimePaymentCardCreate } from "../../query/paymentCards/mutations/useMutationCompleteOneTimePaymentCardCreate";
import { useMutationCompletePaymentCardCreate } from "../../query/paymentCards/mutations/useMutationCompletePaymentCardCreate";
import { useMutationInitiateOneTimePaymentCardCreate } from "../../query/paymentCards/mutations/useMutationInitiateOneTimePaymentCardCreate";
import { useMutationInitiatePaymentCardCreate } from "../../query/paymentCards/mutations/useMutationInitiatePaymentCardCreate";
import { WSServiceError } from "../../utils/serviceHelper";
import { footprintAppearanceConfig } from "../constants/footprint";

type Config = {
  clientId?: string;
  oneTime?: boolean;
  /**
   * Routes card capture through the v3 create → link → activate flow
   * (WIN-15665) instead of the v1 initiate/complete handshake. Only the
   * payout-card consumer opts in for now — payment-method (CPE) and
   * one-time/guest capture stay on v1 until their own migrations land.
   */
  v3?: boolean;
};

/**
 * TODO(v3): compliance DOUBLE-CHECK — placeholder consent version for the
 * card-on-file activation (`PaymentCardActivate.consentVersion`). v1 never
 * captured a distinct storage consent for this step, and v3 has no dedicated
 * `AcknowledgementName` for it either, so there's no existing copy or version
 * to reuse. PENDING Legal/Product sign-off on the actual attestation text and
 * version scheme before general availability — see WIN-15665.
 */
const PAYOUT_CARD_ACTIVATION_CONSENT_VERSION = "1.0";

/**
 * What a capture handshake needs to hand back so the card can be completed.
 *
 * `paymentCardVersion` is v3-only: activation is a conditional write and the
 * strong `If-Match` ETag has to come from the create response (the latest write
 * of that card). The v1 initiate/complete handshake carries no version.
 */
type InitiateResult = {
  footprintToken: string;
  paymentCardId: string;
  paymentCardVersion?: number;
};

type OpenConfig = {
  onCancel?: () => void;
  onSuccess?: (paymentCard: IPaymentCard) => void;
  onError?: (error: WSServiceError) => void;
  variant?: "modal" | "inline";
  containerId?: string;
  setAsInstantPayoutMethod?: boolean;
  blockCreditCards?: boolean;
};

export const CREDIT_CARD_INSTANT_PAYOUT_ERROR =
  "Credit cards aren't supported for instant payout. Please add an eligible debit card to continue.";

const PAYMENT_CARD_ACTIVATION_ERROR =
  "We couldn't finish adding this card. Please try again.";

export const usePaymentCardForm = (config?: Config) => {
  const [initiateCreate, initiateCreateMeta] =
    useMutationInitiatePaymentCardCreate();
  const [completeCreate, completeCreateMeta] =
    useMutationCompletePaymentCardCreate();
  const [initiateOneTimeCreate, initiateOneTimeCreateMeta] =
    useMutationInitiateOneTimePaymentCardCreate();
  const [completeOneTimeCreate, completeOneTimeCreateMeta] =
    useMutationCompleteOneTimePaymentCardCreate();

  const [createCardV3, createCardV3Meta] =
    queriesPaymentCards.createPaymentCard.useMutation();
  const [createCardLinkV3, createCardLinkV3Meta] =
    queriesPaymentCards.createPaymentCardLink.useMutation();
  const [activateCardV3, activateCardV3Meta] =
    queriesPaymentCards.activatePaymentCard.useMutation();

  const { openSnackbar } = useWSSnackbar();
  const session = useSession();

  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const oneTimeUserId = useMemo(
    () => (config?.oneTime ? config.clientId : undefined),
    [config?.oneTime, config?.clientId]
  );
  // Captured outside `complete`, whose own parameter is also named `config`
  // (the OpenConfig) and would otherwise shadow this hook-level flag.
  const isV3 = !!config?.v3;

  const initiate = useCallback(async (): Promise<
    InitiateResult | undefined
  > => {
    if (oneTimeUserId) {
      return initiateOneTimeCreate({
        userId: oneTimeUserId
      });
    }

    if (isV3) {
      const createIdempotencyKey = new IdempotencyKey({
        accountId: session.accountId,
        step: "createPaymentCard"
      });
      const card = await createCardV3({
        idempotencyKey: createIdempotencyKey.value,
        paymentCardCreate: {}
      });
      if (!card) return;
      createIdempotencyKey.destroy();

      const linkIdempotencyKey = new IdempotencyKey({
        paymentCardId: card.id,
        step: "createPaymentCardLink"
      });
      const link = await createCardLinkV3({
        paymentCardId: card.id,
        idempotencyKey: linkIdempotencyKey.value
      });
      if (!link) return;
      linkIdempotencyKey.destroy();

      return {
        footprintToken: link.clientToken,
        paymentCardId: card.id,
        paymentCardVersion: card.version
      };
    }

    return initiateCreate();
  }, [
    isV3,
    createCardLinkV3,
    createCardV3,
    initiateCreate,
    initiateOneTimeCreate,
    oneTimeUserId,
    session.accountId
  ]);

  const [setDefaultInstantPayoutMethod, setDefaultInstantPayoutMethodMeta] =
    useWSMutation<void, WSServiceError, { paymentCardId: string }>(
      async ({ paymentCardId }) => {
        const payoutSettingsId = queryPayoutSettings.data?.id;

        if (!payoutSettingsId) return;

        await payoutsApi.updatePayoutSettings({
          payoutSettingsId,
          payoutSettingsUpdate: {
            preferredPayoutSpeed: PreferredPayoutSpeed.INSTANT,
            instantDestination: {
              id: paymentCardId,
              type: PayoutDestinationType.PAYMENT_CARD
            }
          }
        });
      },
      {
        dependencies: [
          queriesPayouts.listPayoutSettings.queryKeyBase,
          queriesPayouts.getPayoutSettings.queryKeyBase
        ]
      }
    );

  const complete = useCallback(
    async (initiated: InitiateResult, openConfig?: OpenConfig) => {
      const { paymentCardId } = initiated;

      if (oneTimeUserId) {
        return completeOneTimeCreate(
          {
            paymentCardId,
            userId: oneTimeUserId
          },
          { onSuccess: openConfig?.onSuccess, onError: openConfig?.onError }
        );
      }

      if (isV3) {
        if (initiated.paymentCardVersion === undefined) {
          // The create response always carries a version, so this only fires if
          // that contract breaks. Say so rather than send an `If-Match` the
          // route rejects: a 422 here leaves the card Pending — created, never
          // activated, and filtered out of every list the user can see.
          openSnackbar({
            message: PAYMENT_CARD_ACTIVATION_ERROR,
            type: "error"
          });
          return;
        }

        const activateIdempotencyKey = new IdempotencyKey({
          paymentCardId,
          step: "activatePaymentCard"
        });

        return activateCardV3(
          {
            paymentCardId,
            idempotencyKey: activateIdempotencyKey.value,
            ifMatch: strongETag(initiated.paymentCardVersion),
            paymentCardActivate: {
              accepted: true,
              consentVersion: PAYOUT_CARD_ACTIVATION_CONSENT_VERSION
            }
          },
          {
            onSuccess: paymentCard => {
              activateIdempotencyKey.destroy();

              const shouldBlockIneligibleCard =
                openConfig?.blockCreditCards ||
                openConfig?.setAsInstantPayoutMethod;

              if (
                shouldBlockIneligibleCard &&
                !selectorPaymentCardPayoutReady(paymentCard)
              ) {
                openSnackbar({
                  message: CREDIT_CARD_INSTANT_PAYOUT_ERROR,
                  type: "error"
                });
                return;
              }

              // v3 `PaymentCard` has no `paymentCardId` field (it's `id`); alias
              // it so every consumer of the shared `onSuccess` contract — v1 or
              // v3 branch — can keep reading `paymentCard.paymentCardId`.
              const paymentCardForConsumer = {
                ...paymentCard,
                paymentCardId: paymentCard.id
              } as unknown as IPaymentCard;

              if (openConfig?.setAsInstantPayoutMethod) {
                setDefaultInstantPayoutMethod(
                  {
                    paymentCardId
                  },
                  {
                    onSuccess: () => {
                      openConfig?.onSuccess?.(paymentCardForConsumer);
                    },
                    onError: error =>
                      openConfig?.onError?.(error as unknown as WSServiceError)
                  }
                );
              } else {
                openConfig?.onSuccess?.(paymentCardForConsumer);
              }
            },
            onError: error =>
              openConfig?.onError?.(error as unknown as WSServiceError)
          }
        );
      }

      return completeCreate(
        {
          paymentCardId,
          saveForFutureUse: true
        },
        {
          onSuccess: paymentCard => {
            const shouldBlockCreditCard =
              openConfig?.blockCreditCards ||
              openConfig?.setAsInstantPayoutMethod;

            if (
              shouldBlockCreditCard &&
              paymentCard.cardType === PaymentCardType.Credit
            ) {
              openSnackbar({
                message: CREDIT_CARD_INSTANT_PAYOUT_ERROR,
                type: "error"
              });
              return;
            }

            if (openConfig?.setAsInstantPayoutMethod) {
              setDefaultInstantPayoutMethod(
                {
                  paymentCardId
                },
                {
                  onSuccess: () => {
                    openConfig?.onSuccess?.(paymentCard);
                  },
                  onError: openConfig?.onError
                }
              );
            } else {
              openConfig?.onSuccess?.(paymentCard);
            }
          },
          onError: openConfig?.onError
        }
      );
    },
    [
      activateCardV3,
      completeCreate,
      completeOneTimeCreate,
      isV3,
      oneTimeUserId,
      openSnackbar,
      setDefaultInstantPayoutMethod
    ]
  );

  const open = useCallback(async (config?: OpenConfig) => {
    const paymentCardCreateInitiate = await initiate();

    if (!paymentCardCreateInitiate) return;

    const component = footprint.init({
      appearance: footprintAppearanceConfig,
      authToken: paymentCardCreateInitiate.footprintToken,
      kind: FootprintComponentKind.Form,
      variant: config?.variant || "modal",
      containerId: config?.containerId || "root",
      onCancel: config?.onCancel,
      onClose: config?.onCancel,
      options: {
        hideFootprintLogo: true
      },
      onComplete: () => {
        complete(paymentCardCreateInitiate, config);

        component.destroy();
      }
    });

    component.render();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = useMemo(
    () =>
      initiateCreateMeta.isLoading ||
      completeCreateMeta.isLoading ||
      initiateOneTimeCreateMeta.isLoading ||
      completeOneTimeCreateMeta.isLoading ||
      createCardV3Meta.isLoading ||
      createCardLinkV3Meta.isLoading ||
      activateCardV3Meta.isLoading ||
      setDefaultInstantPayoutMethodMeta.isLoading,
    [
      initiateCreateMeta.isLoading,
      completeCreateMeta.isLoading,
      initiateOneTimeCreateMeta.isLoading,
      completeOneTimeCreateMeta.isLoading,
      createCardV3Meta.isLoading,
      createCardLinkV3Meta.isLoading,
      activateCardV3Meta.isLoading,
      setDefaultInstantPayoutMethodMeta.isLoading
    ]
  );

  return {
    open,
    isLoading
  };
};
